import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/types/user";
import { useRoleAccessOptimized } from "@/hooks/use-role-access-optimized";

interface RoleRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
}

/**
 * Composant de route protégée par rôle
 *
 * Phase 3: Non-blocking auth - Uses section loader, not fullscreen.
 * Public pages render immediately. Only protected routes wait for auth.
 *
 * @param children - Composants à afficher si l'accès est autorisé
 * @param allowedRoles - Liste des rôles autorisés à accéder à la route
 */
const RoleRoute = ({ children, allowedRoles }: RoleRouteProps) => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { hasAccess, isLoading, redirectPath } = useRoleAccessOptimized(allowedRoles);

  // Phase 3: Section loader for protected routes ONLY (not fullscreen)
  // This allows public pages to render immediately while auth restores
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center py-24 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600 text-sm">Vérification des accès...</p>
        </div>
      </div>
    );
  }

  // Gérer la redirection pour l'email non vérifié
  if (redirectPath === "/auth/verify-email") {
    navigate("/auth/verify-email", { replace: true });
    return null;
  }

  // Rediriger vers login si pas d'utilisateur (seulement après que l'auth soit chargée)
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  // Rediriger si pas d'accès
  if (!hasAccess && redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // Afficher les enfants si accès autorisé
  return <>{children}</>;
};

export default RoleRoute;
